import React from "react";
import "../styles/YOE.css";
import FuturoPerfecto from "../assets/companies/futuro_perfecto_innovacion_logo.jpeg";
import Nanu from "../assets/companies/Nanu_Logo.jpeg";
import KolanutAfrica from "../assets/companies/Kolanut_Africa_Logo.jpeg";
import ThePrimeOptimum from "../assets/companies/The_Prime_Logo.jpeg";
import { FaCalendarAlt, FaMapMarkerAlt, FaBriefcase } from "react-icons/fa";

export default function WorkExperience() {
  const experiences = [
    {
      company: "Futuro Perfecto",
      position: "Software Engineer",
      duration: "January 2025 – December 2025",
      location: "Spain, Madrid · Remote",
      description: [
        "Led the end-to-end development of FISHES AI, an AI-powered grant proposal automation platform used by EU-funding applicants. Designed, built, deployed, and scaled the entire system across backend, cloud, and AI pipelines.",
        "Developed a European funding automation platform that identifies calls, generates proposals, and handles project management. Enabling startups and SMEs to cut proposal prep time from 6 weeks to 2 days.",
        "Designed, deployed, and scaled the entire system infrastructure on AWS, enhancing reliability and scalability, achieving 99.9% uptime while overcoming vendor limitations and optimizing cloud resource utilization for performance and cost efficiency.",
        "Implemented Infrastructure as Code (IaC) using Terraform and Docker on AWS, automating environment provisioning, reducing deployment time from 30 to 5 minutes, and cutting cloud cost by 40% through optimized infrastructure design.",
        "Optimized database performance on PostgreSQL through query tuning, connection pooling, and caching strategies, improving read and write efficiency by 60% and ensuring seamless handling of high-volume analytical workloads.",
        "Continuously optimize system performance through profiling, caching, and asynchronous processing improvements, ensuring the platform remains responsive under high-load scenarios and scales efficiently with growing user demands.",
        "Architected CI/CD pipelines using GitHub Actions and AWS Code Pipeline, enabling automated testing, versioned deployments, and rollback mechanisms, resulting in faster, more reliable release cycles across development and production environments.",
        "Integrated GPT-4 into backend pipelines to automate content generation and research workflows, eliminating ~80% of manual writing work while maintaining EU compliance.",
        "Collaborated with cross-functional stakeholders to define functional requirements, refine product workflows, and deliver a production-ready platform from ground zero.",
      ],
      skills: [
        "Python",
        "FastAPI",
        "PostgreSQL",
        "AWS",
        "Terraform",
        "Docker",
        "GitHub Actions",
        "CI/CD",
        "GPT-4",
        "AI/ML",
        "Infrastructure as Code",
      ],
      logo: FuturoPerfecto,
      type: "Full-time",
    },
    {
      company: "Nanu",
      position: "Software Developer Intern",
      duration: "September 2024 – December 2024",
      location: "London, UK",
      description: [
        "Developed and optimized React-based dashboards for an AI communication coach app, streaming real-time meeting insights for audio/video calls with low-latency updates.",
        "Integrated NLP-driven sentiment, engagement, and language analysis into the frontend by collaborating with AI/ML engineers, ensuring real-time visualization of live meeting metrics.",
        "Built interactive components to display live meeting summaries and AI-generated action points, improving post-meeting clarity and user adoption.",
        "Enhanced dashboard performance with frontend optimizations (state management, rendering), achieving 25% faster responsiveness across devices.",
      ],
      skills: [
        "Python",
        "FastAPI",
        "React",
        "JavaScript",
        "AWS",
        "AI/ML",
        "NLP",
        "Real-time Feedback",
      ],
      logo: Nanu,
      type: "Internship",
    },

    {
      company: "Kolanut Africa",
      position: "Frontend Developer (Volunteer)",
      duration: "May 2024 – Aug 2024",
      location: "Lagos, Nigeria",
      description: [
        "Worked closely with the design team to create user-centric front-end interfaces that improve navigation speed by 30%, resulting in a more streamlined experience for users during testing phases.",
        "Implemented interactive prototype elements that cut average page loading time from five seconds down to two seconds.",
        "Integrated APIs to onboard 500+ waitlisted users, and automate personalized email updates based on user milestones.",
      ],
      skills: ["React", "TypeScript", "Next.js", "Express", "MongoDB"],
      logo: KolanutAfrica,
      type: "Volunteer",
    },
    {
      company: "The Prime Optimum",
      position: "Frontend Developer (Volunteer)",
      duration: "January 2023 – August 2023",
      location: "Ekiti, Nigeria",
      description: [
        "Developed a React.js web application for ICAN students, providing 24/7 access to study resources and increasing student engagement by 40% across web and mobile.",
        "Engineered a secure Node.js/Express API with role-based access control (RBAC), safeguarding student and tutor data while sustaining 100% uptime during peak exam sessions.",
        "Implemented an admin dashboard for managing student registrations, class schedules, and performance tracking, reducing manual administrative work by over 60%.",
        "Improved frontend performance with code-splitting, lazy loading, and optimized state management, reducing page load times by 35% and boosting student retention.",
        "Digitized 50+ learning modules and mock tests in collaboration with tutors, creating interactive content that improved student pass rates year-over-year.",
      ],
      skills: ["JavaScript", "Node.js", "Express", "Responsive Design"],
      logo: ThePrimeOptimum,
      type: "Volunteer",
    },
  ];

  return (
    <section id="experience" className="experience-section">
      <div className="experience-container">
        <div className="experience-header">
          <h2 className="experience-title">Professional Experience</h2>
          <p className="experience-subtitle">
            My journey through diverse roles and companies, showcasing growth in
            software development, AI/ML technologies, and innovative
            problem-solving across different industries.
          </p>
        </div>

        <div className="experience-grid">
          {experiences.map((exp, idx) => (
            <div className="experience-card" key={idx}>
              <div className="card-header">
                <div className="company-logo">
                  <img src={exp.logo} alt={exp.company} />
                </div>
                <div className="card-badge">
                  <FaBriefcase />
                  {exp.type}
                </div>
              </div>

              <div className="card-content">
                <div className="position-info">
                  <h3 className="position-title">{exp.position}</h3>
                  <h4 className="company-name">{exp.company}</h4>
                </div>

                <div className="experience-meta">
                  <div className="meta-item">
                    <FaCalendarAlt />
                    <span>{exp.duration}</span>
                  </div>
                  <div className="meta-item">
                    <FaMapMarkerAlt />
                    <span>{exp.location}</span>
                  </div>
                </div>

                <div className="description">
                  <ul className="description-list">
                    {exp.description.map((pt, i) => (
                      <li key={i}>{pt}</li>
                    ))}
                  </ul>
                </div>

                <div className="skills">
                  <h5>Technologies</h5>
                  <div className="skill-tags">
                    {exp.skills.map((skill, i) => (
                      <span className="skill-tag" key={i}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
